// pages/display/ThreeDim.js —— 展示中心 · 三维可视化（doc01 2.5 p13–p14）
// 三层层级下钻：站点 → 箱体（储能集装箱）→ 设备（点击节点进入下一层，顶部下拉可切换）
// 站点层：基本信息 / 收益指标 / 电量指标（日总收益、日充放电量近 7 日）/ 实时功率（今昨对比）
//        / 告警统计（每日告警次数近 7 日）/ 今日告警（实时告警可跳转故障报警）
// 告警图标配色：最高 A 级红 / B 级橙 / C 级黄 / 未分级蓝；点击图标弹窗展示该集装箱下全部实时告警
// 箱体层：返回 / 切换集装箱、箱内实时数据浮窗（堆 SOC 平均 / PCS 功率之和 / BMS 温度平均）
// 设备层：返回 / 切换设备、设备实时运行数据与今日告警，电池簇可查看簇内单体电压 / 温度 / SOC / SOH
// 底部按钮：显示/隐藏告警、显示/隐藏数据、默认视角、隐藏/显示面板
// 说明：本页为前端演示，暂以 CSS 伪三维场景呈现，正式环境接入 WebGL 三维引擎与场站模型。
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Button, Tag, Table, Modal, Input, Space, message } from 'antd';
import {
  LeftOutlined, AlertOutlined, ThunderboltOutlined, DatabaseOutlined,
  EyeOutlined, EyeInvisibleOutlined, ReloadOutlined, LoginOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import PageHead from '../../components/PageHead';
import {
  STATIONS, getStation, getOverview, getDevices,
  getSingleIncome, getSingleEnergy, getSingleAlarm, getRealtimeDetail, getFaultAlarmList,
} from '../../config/stationData';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TIP = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const DEMO_PWD = '123456';

// 告警等级配色（doc01 2.5 p14）：A 红 / B 橙 / C 黄 / 未分级 蓝
const LEVEL_COLOR = { A: 'red', B: 'orange', C: 'gold', 未分级: 'blue' };
const LEVEL_HEX = { A: '#ff5a7f', B: '#f4894a', C: '#f4c542', 未分级: '#4dd8ff' };
const LEVEL_RANK = { A: 0, B: 1, C: 2, 未分级: 3 };
const STATUS_COLOR = { 发生中: 'red', 已结束: 'green' };

const avgOf = (list, key) => (list.length ? Math.round(list.reduce((s, x) => s + (x[key] || 0), 0) / list.length) : 0);
const sumOf = (list, key) => list.reduce((s, x) => s + (x[key] || 0), 0);

// 最高告警等级（用于箱体告警图标）
const topLevel = (alarms) => {
  if (!alarms.length) return null;
  return alarms.reduce((acc, a) => (LEVEL_RANK[a.level] < LEVEL_RANK[acc] ? a.level : acc), alarms[0].level);
};

const ThreeDim = () => {
  const navigate = useNavigate();
  const [stationId, setStationId] = useState(STATIONS[0].id);
  const [level, setLevel] = useState('station'); // station | box | device
  const [boxIdx, setBoxIdx] = useState(0);
  const [deviceKey, setDeviceKey] = useState('');
  const [showAlarm, setShowAlarm] = useState(true);
  const [showData, setShowData] = useState(true);
  const [panelHidden, setPanelHidden] = useState(false);
  const [alarmBox, setAlarmBox] = useState(null);   // 告警弹窗：该集装箱下全部实时告警
  const [stopBox, setStopBox] = useState(null);     // 紧急停机：二次口令
  const [stopPwd, setStopPwd] = useState('');

  const station = getStation(stationId);
  const ov = getOverview(stationId);
  const dev = getDevices(stationId);
  const income = getSingleIncome(stationId)['近7日'];
  const energy = getSingleEnergy(stationId)['近7日'];
  const alarmTrend = getSingleAlarm(stationId).trend;
  const detail = getRealtimeDetail(stationId);
  const stationAlarms = getFaultAlarmList().filter((a) => a.station === station.name);

  // 箱体（储能集装箱）由设备按舱位拆分（演示口径，待接口返回真实舱位）
  const half = Math.ceil(dev.racks.length / 2);
  const boxes = [
    {
      id: 'CT-1', name: '储能集装箱 #1',
      racks: dev.racks.slice(0, half), pcs: dev.pcs.slice(0, half), hvac: dev.hvac.slice(0, 1),
    },
    {
      id: 'CT-2', name: '储能集装箱 #2',
      racks: dev.racks.slice(half), pcs: dev.pcs.slice(half), hvac: dev.hvac.slice(1),
    },
  ];
  // 集装箱下的实时告警（演示：按顺序拆分站点实时告警）
  const boxAlarms = (i) => stationAlarms.filter((_, k) => k % boxes.length === i);
  const curBox = boxes[boxIdx] || boxes[0];

  // 设备层可选设备（当前集装箱内的电池堆 / PCS / 空调）
  const boxDevices = [
    ...curBox.racks.map((r) => ({ key: r.id, name: r.name, kind: 'BMS', data: r })),
    ...curBox.pcs.map((p) => ({ key: p.id, name: p.name, kind: 'PCS', data: p })),
    ...curBox.hvac.map((h) => ({ key: h.id, name: h.name, kind: '空调', data: h })),
  ];
  const curDevice = boxDevices.find((d) => d.key === deviceKey) || boxDevices[0];

  const enterBox = (i) => { setBoxIdx(i); setLevel('box'); setDeviceKey(''); };
  const enterDevice = (key) => { setDeviceKey(key); setLevel('device'); };
  const backToStation = () => { setLevel('station'); setDeviceKey(''); };
  const backToBox = () => { setLevel('box'); setDeviceKey(''); };

  // 默认视角：回到站点层并复位底部显示开关
  const resetView = () => {
    setLevel('station');
    setBoxIdx(0);
    setDeviceKey('');
    setShowAlarm(true);
    setShowData(true);
    message.success('已回到默认视角');
  };

  const confirmStop = () => {
    if (stopPwd !== DEMO_PWD) { message.error('口令错误（口令：123456）'); return; }
    message.success(`${stopBox.name} 紧急停机指令已下发`);
    setStopBox(null);
    setStopPwd('');
  };

  const chartPanel = (title, extra, children) => (
    <div className="panel">
      <div className="panel-title">{title}</div>
      {children}
    </div>
  );

  // ---------------- 站点层 ----------------
  const stationLevel = (
    <>
      <div className="kpi-grid td-kpi5">
        <div className="kpi-card"><div className="kpi-label">累计收益</div><div className="kpi-value">{(ov.incomeTotal / 10000).toFixed(2)}<span className="unit">万元</span></div><div className="kpi-foot">不含今日</div></div>
        <div className="kpi-card"><div className="kpi-label">昨日收益</div><div className="kpi-value">{ov.incomeYesterday.toLocaleString()}<span className="unit">元</span></div></div>
        <div className="kpi-card"><div className="kpi-label">累计充电量</div><div className="kpi-value">{ov.chargeTotal}<span className="unit">MWh</span></div><div className="kpi-foot">不含今日</div></div>
        <div className="kpi-card"><div className="kpi-label">累计放电量</div><div className="kpi-value">{ov.dischargeTotal}<span className="unit">MWh</span></div><div className="kpi-foot">不含今日</div></div>
        <div className="kpi-card">
          <div className="kpi-label">今日告警</div>
          <div className="kpi-value">{stationAlarms.filter((a) => a.status === '发生中').length}<span className="unit">条</span></div>
          <div className="kpi-foot">
            <span style={{ color: '#4dd8ff', cursor: 'pointer' }} onClick={() => navigate('/app/alarm/fault')}>实时告警 〉</span>
          </div>
        </div>
      </div>

      <div className="grid-2">
        {chartPanel('日总收益（近 7 日，不含今天）', '单位：元',
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={income} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TIP} />
              <Bar dataKey="income" name="收益" fill="#f4c542" radius={[4, 4, 0, 0]} barSize={18} />
            </ComposedChart>
          </ResponsiveContainer>)}
        {chartPanel('日充放电量（近 7 天，不含今天）', '单位：kWh',
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={energy} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TIP} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
              <Bar dataKey="charge" name="充电量" fill="#52e8b0" radius={[4, 4, 0, 0]} barSize={14} />
              <Bar dataKey="discharge" name="放电量" fill="#4dd8ff" radius={[4, 4, 0, 0]} barSize={14} />
            </ComposedChart>
          </ResponsiveContainer>)}
      </div>

      <div className="grid-2">
        {chartPanel('实时功率（今日 / 昨日对比）', '单位：kW · X 轴 今日0点~明日6点',
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={detail.power} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} interval={2} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TIP} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
              {/* 功率曲线统一按直线段绘制，避免样条平滑造成非真实过冲 */}
              <Line type="linear" dataKey="today" name="今日(kW)" stroke="#00d4ff" strokeWidth={2} dot={false} />
              <Line type="linear" dataKey="yesterday" name="昨日(kW)" stroke="#8b7cf6" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>)}
        {chartPanel('每日告警次数统计（近 7 日，不含今天）', '单位：次',
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={alarmTrend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TIP} />
              <Bar dataKey="count" name="告警次数" fill="#ff5a7f" radius={[4, 4, 0, 0]} barSize={18} />
            </ComposedChart>
          </ResponsiveContainer>)}
      </div>

      <div className="panel">
        <div className="panel-title">
          今日告警
          <span style={{ color: '#4dd8ff', cursor: 'pointer', fontSize: 12 }} onClick={() => navigate('/app/alarm/fault')}>实时告警 〉</span>
        </div>
        <Table
          rowKey="key"
          size="small"
          dataSource={stationAlarms}
          pagination={false}
          scroll={{ x: 760 }}
          columns={[
            { title: '发生时间', dataIndex: 'time', width: 170 },
            { title: '等级', dataIndex: 'levelText', width: 120, render: (v, r) => <Tag color={LEVEL_COLOR[r.level]}>{v}</Tag> },
            { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
            { title: '设备名称', dataIndex: 'device', width: 170 },
            { title: '告警标题', dataIndex: 'title', strong: true },
          ]}
        />
      </div>
    </>
  );

  // ---------------- 箱体层 ----------------
  const boxLevel = (
    <>
      <div className="grid-3">
        <div className="kpi-card"><div className="kpi-label">箱内堆 SOC 平均值</div><div className="kpi-value">{avgOf(curBox.racks, 'soc')}<span className="unit">%</span></div><div className="kpi-foot">{curBox.name} · {curBox.racks.length} 个电池堆</div></div>
        <div className="kpi-card"><div className="kpi-label">箱内 PCS 功率之和</div><div className="kpi-value">{sumOf(curBox.pcs, 'p')}<span className="unit">kW</span></div><div className="kpi-foot">{curBox.pcs.length} 台 PCS</div></div>
        <div className="kpi-card"><div className="kpi-label">箱内 BMS 温度平均值</div><div className="kpi-value">{avgOf(curBox.racks, 'temp')}<span className="unit">℃</span></div><div className="kpi-foot">电池堆平均温度</div></div>
      </div>

      <div className="panel">
        <div className="panel-title">箱内设备</div>
        <div className="dev-grid">
          {boxDevices.map((d) => (
            <div
              key={d.key}
              className="dev-card"
              style={{ cursor: 'pointer' }}
              onClick={() => enterDevice(d.key)}
            >
              <div className="dev-card-head">
                <span className={`dot ${d.kind === 'BMS' && d.data.alarm ? 'fault' : 'online'}`} />
                <span className="dev-name">{d.name}</span>
                <span className="dev-status">{d.kind}</span>
              </div>
              <div className="dev-kv">
                {d.kind === 'BMS' ? (
                  <>
                    <div className="dev-kv-row"><span className="dev-kv-k">工作状态</span><span className="dev-kv-v">{d.data.workState}</span></div>
                    <div className="dev-kv-row"><span className="dev-kv-k">SOC</span><span className="dev-kv-v">{d.data.soc} %</span></div>
                    <div className="dev-kv-row"><span className="dev-kv-k">温度</span><span className="dev-kv-v">{d.data.temp} ℃</span></div>
                  </>
                ) : d.kind === 'PCS' ? (
                  <>
                    <div className="dev-kv-row"><span className="dev-kv-k">运行状态</span><span className="dev-kv-v">{d.data.running ? '运行' : '停机'}</span></div>
                    <div className="dev-kv-row"><span className="dev-kv-k">有功功率</span><span className="dev-kv-v">{d.data.p} kW</span></div>
                    <div className="dev-kv-row"><span className="dev-kv-k">并网状态</span><span className="dev-kv-v">{d.data.grid}</span></div>
                  </>
                ) : (
                  <>
                    <div className="dev-kv-row"><span className="dev-kv-k">工作模式</span><span className="dev-kv-v">{d.data.mode || '—'}</span></div>
                    <div className="dev-kv-row"><span className="dev-kv-k">设定温度</span><span className="dev-kv-v">{d.data.setTemp != null ? `${d.data.setTemp} ℃` : '—'}</span></div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">本集装箱实时告警<small>{boxAlarms(boxIdx).length} 条</small></div>
        <Table
          rowKey="key"
          size="small"
          dataSource={boxAlarms(boxIdx)}
          pagination={false}
          scroll={{ x: 700 }}
          columns={[
            { title: '发生时间', dataIndex: 'time', width: 170 },
            { title: '等级', dataIndex: 'levelText', width: 120, render: (v, r) => <Tag color={LEVEL_COLOR[r.level]}>{v}</Tag> },
            { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
            { title: '告警标题', dataIndex: 'title', strong: true },
          ]}
        />
      </div>
    </>
  );

  // ---------------- 设备层 ----------------
  const deviceLevel = curDevice ? (
    curDevice.kind === 'BMS' ? (
      <>
        <div className="panel">
          <div className="panel-title">{curDevice.name} · 实时运行数据<small>{curBox.name}</small></div>
          <div className="strat-grid">
            <div className="strat-item"><div className="strat-item-label">工作状态</div><div className="strat-item-value">{curDevice.data.workState}</div></div>
            <div className="strat-item"><div className="strat-item-label">SOC</div><div className="strat-item-value">{curDevice.data.soc}<span className="unit">%</span></div></div>
            <div className="strat-item"><div className="strat-item-label">SOH</div><div className="strat-item-value">{curDevice.data.soh.toFixed(1)}<span className="unit">%</span></div></div>
            <div className="strat-item"><div className="strat-item-label">电压</div><div className="strat-item-value">{curDevice.data.voltage}<span className="unit">V</span></div></div>
            <div className="strat-item"><div className="strat-item-label">电流</div><div className="strat-item-value">{curDevice.data.current}<span className="unit">A</span></div></div>
            <div className="strat-item"><div className="strat-item-label">温度</div><div className="strat-item-value">{curDevice.data.temp}<span className="unit">℃</span></div></div>
          </div>
        </div>
        <div className="panel">
          <div className="panel-title">簇内单体数据<small>共 {dev.cells.length} 个单体</small></div>
          <Table
            size="small"
            rowKey="no"
            dataSource={dev.cells}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 640 }}
            columns={[
              { title: '单体号', dataIndex: 'no', width: 100 },
              { title: '电压 (V)', dataIndex: 'voltage', width: 120 },
              { title: '温度 (℃)', dataIndex: 'temp', width: 120 },
              { title: 'SOC (%)', dataIndex: 'soc', width: 120 },
              { title: 'SOH (%)', dataIndex: 'soh', width: 120 },
            ]}
          />
        </div>
      </>
    ) : (
      <div className="panel">
        <div className="panel-title">{curDevice.name} · 实时运行数据<small>{curBox.name}</small></div>
        <div className="strat-grid">
          {curDevice.kind === 'PCS' ? (
            <>
              <div className="strat-item"><div className="strat-item-label">运行状态</div><div className="strat-item-value">{curDevice.data.running ? '运行' : '停机'}</div></div>
              <div className="strat-item"><div className="strat-item-label">有功功率</div><div className="strat-item-value">{curDevice.data.p}<span className="unit">kW</span></div></div>
              <div className="strat-item"><div className="strat-item-label">无功功率</div><div className="strat-item-value">{curDevice.data.q}<span className="unit">kVar</span></div></div>
              <div className="strat-item"><div className="strat-item-label">功率因数</div><div className="strat-item-value">{curDevice.data.pf}</div></div>
              <div className="strat-item"><div className="strat-item-label">今日充电量</div><div className="strat-item-value">{curDevice.data.chargeToday}<span className="unit">kWh</span></div></div>
              <div className="strat-item"><div className="strat-item-label">今日放电量</div><div className="strat-item-value">{curDevice.data.dischargeToday}<span className="unit">kWh</span></div></div>
            </>
          ) : (
            <>
              <div className="strat-item"><div className="strat-item-label">工作模式</div><div className="strat-item-value">{curDevice.data.mode || '—'}</div></div>
              <div className="strat-item"><div className="strat-item-label">设定温度</div><div className="strat-item-value">{curDevice.data.setTemp != null ? `${curDevice.data.setTemp} ℃` : '—'}</div></div>
              <div className="strat-item"><div className="strat-item-label">回风温度</div><div className="strat-item-value">{curDevice.data.returnTemp != null ? `${curDevice.data.returnTemp} ℃` : '—'}</div></div>
              <div className="strat-item"><div className="strat-item-label">环境湿度</div><div className="strat-item-value">{curDevice.data.humidity != null ? `${curDevice.data.humidity} %RH` : '—'}</div></div>
            </>
          )}
        </div>
      </div>
    )
  ) : null;

  const levelPanels = level === 'station' ? stationLevel : level === 'box' ? boxLevel : deviceLevel;

  // 顶部层级导航：站点 → 箱体 → 设备
  const navBar = (
    <div className="sm-bar">
      <span className="sm-bar-label">层级</span>
      <Space size={6}>
        <Tag color={level === 'station' ? 'cyan' : 'default'} style={{ cursor: 'pointer' }} onClick={backToStation}>站点</Tag>
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>〉</span>
        <Tag color={level === 'box' ? 'cyan' : 'default'} style={{ cursor: level === 'station' ? 'default' : 'pointer' }} onClick={() => level !== 'station' && backToBox()}>{curBox.name}</Tag>
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>〉</span>
        <Tag color={level === 'device' ? 'cyan' : 'default'}>{curDevice ? curDevice.name : '设备'}</Tag>
      </Space>
      {level !== 'station' ? <Button size="small" icon={<LeftOutlined />} onClick={level === 'device' ? backToBox : backToStation}>返回</Button> : null}
      {level === 'device' ? (
        <Select size="small" value={curDevice ? curDevice.key : undefined} style={{ width: 160 }} onChange={enterDevice}
          options={boxDevices.map((d) => ({ value: d.key, label: `切换设备：${d.name}` }))} />
      ) : null}
      <span className="sm-bar-meta">{station.name} · {station.typeLabel}</span>
    </div>
  );

  // 伪三维场站场景（站点层展示箱体，箱体层展示箱内设备）
  const scene = (
    <div className="scene-stage">
      {level === 'station' ? (
        <div className="three-dim-row">
          {boxes.map((b, i) => {
            const lv = topLevel(boxAlarms(i));
            return (
              <div className="three-dim-box" key={b.id}>
                <div className="three-dim-box-name">
                  {b.name}
                  {showAlarm && lv ? (
                    <span
                      title={`点击查看 ${b.name} 下全部实时告警`}
                      style={{ color: LEVEL_HEX[lv], cursor: 'pointer', marginLeft: 8 }}
                      onClick={(e) => { e.stopPropagation(); setAlarmBox({ box: b, alarms: boxAlarms(i) }); }}
                    >
                      <AlertOutlined /> {lv} 级
                    </span>
                  ) : null}
                </div>
                <div className="three-dim-box-racks">
                  {b.racks.map((r) => (
                    <div className={`three-dim-rack${r.alarm ? ' fault' : ''}`} key={r.id} title={`${r.name} · ${r.workState}`}>
                      <ThunderboltOutlined />
                      <span>{r.name.replace('电池堆 ', '')}</span>
                    </div>
                  ))}
                </div>
                {showData ? (
                  <div className="three-dim-float">
                    <div>SOC 平均 {avgOf(b.racks, 'soc')} %</div>
                    <div>PCS 功率 {sumOf(b.pcs, 'p')} kW</div>
                    <div>BMS 温度 {avgOf(b.racks, 'temp')} ℃</div>
                  </div>
                ) : null}
                <div className="three-dim-box-actions">
                  <Button size="small" type="primary" icon={<LoginOutlined />} onClick={() => enterBox(i)}>进入集装箱</Button>
                  <Button size="small" danger icon={<ThunderboltOutlined />} onClick={() => { setStopBox(b); setStopPwd(''); }}>紧急停机</Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="three-dim-row">
          {boxDevices.map((d) => (
            <div className={`three-dim-box${level === 'device' && curDevice && d.key === curDevice.key ? ' active' : ''}`} key={d.key}>
              <div className="three-dim-box-name">{d.name}<span className="three-dim-kind">{d.kind}</span></div>
              <div className="three-dim-hint">
                {d.kind === 'BMS'
                  ? `工作状态 ${d.data.workState} · SOC ${d.data.soc}% · ${d.data.temp}℃`
                  : d.kind === 'PCS'
                    ? `${d.data.running ? '运行' : '停机'} · ${d.data.p} kW · 并网 ${d.data.grid}`
                    : `${d.data.mode || '—'} · 设定 ${d.data.setTemp != null ? `${d.data.setTemp}℃` : '—'}`}
              </div>
              <div className="three-dim-box-actions">
                <Button size="small" type="primary" icon={<AppstoreOutlined />} onClick={() => enterDevice(d.key)}>查看设备</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="scene-hint">
        层级：站点 → 箱体 → 设备。设备层可查看实时运行数据与今日告警；电池簇可查看簇内所有单体的电压 / 温度 / SOC / SOH。
      </div>
    </div>
  );

  return (
    <div>
      <PageHead
        title="三维可视化"
        desc="站点三维建模可视化（增值功能 · 场站建模需单独收费）"
        extra={(
          <Select
            value={stationId}
            onChange={(v) => { setStationId(v); setLevel('station'); setBoxIdx(0); setDeviceKey(''); }}
            style={{ width: 200 }}
            options={STATIONS.map((s) => ({ value: s.id, label: s.name }))}
          />
        )}
      />

      {panelHidden ? (
        <div className="panel" style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Button type="primary" icon={<EyeOutlined />} onClick={() => setPanelHidden(false)}>显示面板</Button>
        </div>
      ) : (
        <>
          {navBar}
          {scene}

          {/* 站点层基本信息 */}
          {level === 'station' ? (
            <div className="panel">
              <div className="panel-title">站点层级 · 基本信息<small>{station.name}</small></div>
              <div className="dev-kv">
                {detail.info.map(([k, v]) => (
                  <div className="dev-kv-row" key={k}><span className="dev-kv-k">{k}</span><span className="dev-kv-v">{v}</span></div>
                ))}
              </div>
            </div>
          ) : null}

          {levelPanels}

          {/* 底部按钮（doc01 2.5 p14） */}
          <div className="sm-bar">
            <Button size="small" icon={showAlarm ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setShowAlarm((v) => !v)}>
              {showAlarm ? '隐藏告警' : '显示告警'}
            </Button>
            <Button size="small" icon={showData ? <EyeInvisibleOutlined /> : <DatabaseOutlined />} onClick={() => setShowData((v) => !v)}>
              {showData ? '隐藏数据' : '显示数据'}
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={resetView}>默认视角</Button>
            <Button size="small" icon={<EyeInvisibleOutlined />} onClick={() => setPanelHidden(true)}>隐藏面板</Button>
            <span className="sm-bar-meta">
              当前层级：{level === 'station' ? '站点' : level === 'box' ? `箱体 · ${curBox.name}` : `设备 · ${curDevice ? curDevice.name : '—'}`}
              {level === 'station' ? ` · 箱体 ${boxes.length} 个` : ` · 设备 ${boxDevices.length} 个`}
              {level === 'device' && curDevice && curDevice.kind === 'BMS' ? ` · 单体 ${dev.cells.length} 个` : ''}
            </span>
          </div>
        </>
      )}

      {/* 告警弹窗：该集装箱下全部实时告警 */}
      <Modal
        title={alarmBox ? `${alarmBox.box.name} · 全部实时告警` : '实时告警'}
        open={!!alarmBox}
        onCancel={() => setAlarmBox(null)}
        footer={<Button type="primary" onClick={() => setAlarmBox(null)}>关闭</Button>}
        width={760}
      >
        <Table
          rowKey="key"
          size="small"
          dataSource={alarmBox ? alarmBox.alarms : []}
          pagination={false}
          columns={[
            { title: '发生时间', dataIndex: 'time', width: 170 },
            { title: '等级', dataIndex: 'levelText', width: 120, render: (v, r) => <Tag color={LEVEL_COLOR[r.level]}>{v}</Tag> },
            { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
            { title: '告警标题', dataIndex: 'title' },
          ]}
        />
      </Modal>

      {/* 紧急停机：二次口令确认（doc01 2.5 p14） */}
      <Modal
        title="紧急停机 · 二次口令确认"
        open={!!stopBox}
        onCancel={() => { setStopBox(null); setStopPwd(''); }}
        onOk={confirmStop}
        okText="确认停机"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 12, color: 'rgba(255,255,255,0.6)' }}>
          紧急停机将切断 {stopBox ? stopBox.name : ''} 内 PCS 输出，请确认现场安全后操作。口令：<b>123456</b>
        </div>
        <Input.Password
          value={stopPwd}
          onChange={(e) => setStopPwd(e.target.value)}
          onPressEnter={confirmStop}
          placeholder="请输入二次口令"
        />
      </Modal>
    </div>
  );
};

export default ThreeDim;
